import { createSelector } from 'reselect';

// ------------------------------------
// Selectors
// ------------------------------------
export const STORE_KEY = 'seasonLookup';
export const selectSeasonLookupState = state => state[STORE_KEY];
// statType = oneOf['projections', 'predictions', 'stats']
// seasonType = oneOf['preseasons', 'seasons']
export const selectCurrent = (state, statType) => selectSeasonLookupState(state).current[statType];
export const selectYears = (state, seasonType, statType) => {
  if (seasonType === 'preseasons' && (statType === 'stats' || statType === 'predictions')) {
    throw new Error('stats and predictions do not exist for preseasons, maybe you meant seasonType = \'seasons\'?');
  }

  return selectSeasonLookupState(state)[seasonType][statType];
};
export const selectYearsForDropdown = createSelector(
  selectYears,
  years => years.map(year => ({
    key: year,
    value: year,
    text: String(year),
  })),
);

// type = oneOf['projections', 'stats']
export const selectWeeks = (state, year, type) => {
  const weeks = selectSeasonLookupState(state).weeks[year];
  if (!weeks) return [];
  return weeks[type] || [];
};

export const selectWeeksLabel = (state, year) => {
  const weeks = selectSeasonLookupState(state).weeks[year];
  if (typeof (year) !== 'undefined' && typeof weeks !== 'undefined') {
    const labels = weeks.labels;
    const weeksLabel = [...labels];
    return weeksLabel || [];
  }
};

// use maps because stat weeks are weird
export const selectMapOldDropdown = createSelector(
  selectWeeks,
  selectWeeksLabel,
  (weeks, weeksLabel) => weeks.map((week, index) => ({
    key: week,
    value: week,
    text: weekOldMap[week] ? weekOldMap[week] : `Week ${week}`,
  })),
);
export const selectMapNewDropdown = createSelector(
  selectWeeks,
  selectWeeksLabel,
  (weeks, weeksLabel) => weeks.map((week, index) => ({
    key: week,
    value: week,
    text: weekNewMap[week] ? weekNewMap[week] : `Week ${week}`,
  })),
);

export const selectWeeksForDropdown = createSelector(
  selectWeeks,
  selectWeeksLabel,
  (weeks, weeksLabel) => weeks.map((week, index) => ({
    key: week,
    value: week,
    text: weeksLabel[index] ? weeksLabel[index] : `Week ${week}`,
  })),
);

export const selectWeeksWithPreSeasonForDropdown = createSelector(
  selectWeeks,
  selectWeeksLabel,
  (weeks, weeksLabel) => [{ key: 0, value: 0, text: 'Preseason' }].concat(weeks.map((week, index) => ({
    key: week,
    value: week,
    text: weeksLabel[index] ? weeksLabel[index] : `Week ${week}`,
  }))),
);
export const selectWeeksNewWithPreSeasonForDropdown = createSelector(
  selectWeeks,
  selectWeeksLabel,
  (weeks, weeksLabel) => [{ key: 0, value: 0, text: 'Preseason' }].concat(weeks.map((week, index) => ({
    key: week,
    value: week,
    text: weekNewMap[week] ? weekNewMap[week] : `Week ${week}`,
  }))),
);
const weekOldMap = {
  18: 'Wild Card',
  19: 'Divisional Round',
  20: 'Conference Championships',
  21: 'Super Bowl',
};

const weekNewMap = {
  19: 'Wild Card',
  20: 'Divisional Round',
  21: 'Conference Championships',
  22: 'Super Bowl',
};


// export const selectWeeksForDropdown = createSelector(
//   selectWeeks,
//   weeks => weeks.map(week => ({
//     key: week,
//     value: week,
//     text: weekMap[week] ? weekMap[week] : `Week ${week}`,
//   })),
// );

// export const selectWeeksWithPreSeasonForDropdown = createSelector(
//   selectWeeks,
//   weeks => [{ key: 0, value: 0, text: 'Preseason' }].concat(weeks.map(week => ({
//     key: week,
//     value: week,
//     text: weekMap[week] ? weekMap[week] : `Week ${week}`,
//   }))),
// );

// ------------------------------------
// Reducer
// ------------------------------------
const actions = {};
const initialState = {
  current: {
    projections: {
      season: 0,
      week: 0,
    },
    stats: {
      season: 0,
      week: 0,
    },
    predictions: {
      season: 0,
      week: 0,
    },
  },
  seasons: {
    stats: [],
    projections: [],
    predictions: [],
  },
  preseasons: {
    projections: [],
  },
  weeks: {
    // 2016: {
    //   stats: [],
    //   projections: [],
    //   predictions: [],
    // },
  },
  hasLoaded: false,
};
const seasonLookupReducer = (state = initialState, action) => {
  const handler = actions[action.type];

  return handler ? handler(state, action) : state;
};
export default seasonLookupReducer;

// ------------------------------------
// Load Seasons
// ------------------------------------
export const LOAD_SEASON_LOOKUP_REQUEST = `${STORE_KEY}/LOAD_SEASON_LOOKUP_REQUEST`;
export const LOAD_SEASON_LOOKUP_SUCCESS = `${STORE_KEY}/LOAD_SEASON_LOOKUP_SUCCESS`;
export const LOAD_SEASON_LOOKUP_ERROR = `${STORE_KEY}/LOAD_SEASON_LOOKUP_ERROR`;

export const loadSeasonLookupRequest = () => ({ type: LOAD_SEASON_LOOKUP_REQUEST });
export const loadSeasonLookupSuccess = ({ seasons, current } = {}) => ({
  type: LOAD_SEASON_LOOKUP_SUCCESS,
  payload: { seasons, current },
});
export const loadSeasonLookupError = error => ({
  type: LOAD_SEASON_LOOKUP_ERROR,
  payload: { error },
});

actions[LOAD_SEASON_LOOKUP_SUCCESS] = (
  state,
  { payload: { seasons, current } },
) => ({ ...state,
  ...current && {
    current: {
      projections: {
        season: current.projections_preseason,
        week: current.projections_week,
      },
      stats: {
        season: current.stats_season,
        week: current.stats_week,
      },
      predictions: {
        season: current.predictions_season,
        week: current.predictions_week,
      },
    },
  },

  ...seasons && {
    preseasons: {
      projections: seasons.projections_preseasons,
    },
    seasons: {
      stats: seasons.projections_preseasons,
      projections: seasons.projections_preseasons,
      predictions: seasons.predictions,
    },
  },

});
actions[LOAD_SEASON_LOOKUP_ERROR] = (state, { payload: { error } }) => ({ ...state,
  error,
});

export const loadSeasonLookup = async (dispatch, getState, { api }) => {
  try {
    dispatch(loadSeasonLookupRequest());
    const [
      { current_season: current },
      seasons,
    ] = await Promise.all([
      api.lookups.getCurrentSeason(),
      api.lookups.getSeasons(),
    ]);

    return dispatch(loadSeasonLookupSuccess({ seasons, current }));
  } catch (error) {
    return dispatch(loadSeasonLookupError(error));
  }
};


// ------------------------------------
// Load Weeks
// ------------------------------------
export const LOAD_WEEKS_REQUEST = `${STORE_KEY}/LOAD_WEEKS_REQUEST`;
export const LOAD_WEEKS_SUCCESS = `${STORE_KEY}/LOAD_WEEKS_SUCCESS`;
export const LOAD_WEEKS_ERROR = `${STORE_KEY}/LOAD_WEEKS_ERROR`;

export const loadWeeksRequest = year => ({
  type: LOAD_WEEKS_REQUEST,
  payload: { year },
});
export const loadWeeksSuccess = (year, weeks) => ({
  type: LOAD_WEEKS_SUCCESS,
  payload: { year, weeks },
});
export const loadWeeksError = error => ({
  type: LOAD_WEEKS_ERROR,
  payload: { error },
});

actions[LOAD_WEEKS_SUCCESS] = (state, { payload: { year, weeks } }) => ({ ...state,
  weeks: { ...state.weeks,
    [year]: {
      stats: weeks.stats_weeks,
      projections: weeks.projections_weeks,
      predictions: weeks.predictions_weeks,
      labels: weeks.labels,
      types: weeks.types,
    },
  },
});
actions[LOAD_WEEKS_ERROR] = (state, { payload: { error } }) => ({ ...state,
  error,
});

export const loadWeeks = year => async (dispatch, getState, { api }) => {
  try {
    const weeksExist = !!selectWeeks(getState(), year, 'stats').length;
    if (weeksExist) return Promise.resolve();

    dispatch(loadWeeksRequest(year));
    const weeks = await api.lookups.getSeasonWeeks(year);
    return dispatch(loadWeeksSuccess(year, weeks));
  } catch (error) {
    return dispatch(loadWeeksError(error));
  }
};



// WEBPACK FOOTER //
// ./src/store/reducers/season.js